/**
 * Story viewer: vertical snap reel, one gallery file per slide.
 * Config is injected by Layouts/story.ctp as window.storyConfig.
 */
document.addEventListener("DOMContentLoaded", function () {
  const root = document.querySelector("[data-story]");
  if (!root) {
    return;
  }

  const config = window.storyConfig || {};
  const reel = root.querySelector("[data-reel]");
  const slides = Array.prototype.slice.call(root.querySelectorAll("[data-slide]"));
  const muteButton = root.querySelector("[data-mute]");
  const playButton = root.querySelector("[data-play]");
  const progress = root.querySelector("[data-progress]");
  const progressFill = root.querySelector("[data-progress-fill]");
  const prevButton = root.querySelector("[data-prev]");
  const nextButton = root.querySelector("[data-next]");
  const likeButton = root.querySelector("[data-like]");
  const likeCount = root.querySelector("[data-like-count]");
  const shareButton = root.querySelector("[data-share]");
  const sheet = root.querySelector("[data-sheet]");
  const hint = root.querySelector("[data-hint]");
  const hintDismiss = root.querySelector("[data-hint-dismiss]");
  const errorBox = root.querySelector("[data-error]");
  const stage = root.querySelector("[data-stage]");
  const ad = root.querySelector("[data-ad]");
  const adClose = root.querySelector("[data-ad-close]");

  const MUTE_KEY = "story_muted";
  const HINT_KEY = "story_hint_seen";

  // -1 so the first setActive(0) is not swallowed by the no-op guard
  let current = -1;
  // Sound is on unless the viewer explicitly muted before.
  let muted = readStore(MUTE_KEY) === "1";
  // True while the browser forced us muted (no user gesture yet); the first
  // gesture then turns sound on and restarts the clip so the opening isn't lost.
  let soundBlocked = false;

  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      // private mode: preference simply does not persist
    }
  }

  function toPersianNumber(number) {
    const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
    return number.toString().replace(/\d/g, function (digit) {
      return persianDigits[digit];
    });
  }

  function videoOf(slide) {
    return slide ? slide.querySelector("video") : null;
  }

  //---------- PLAYBACK ----------
  function applyMute() {
    slides.forEach(function (slide) {
      const video = videoOf(slide);
      if (video) {
        video.muted = muted;
      }
    });
    if (muteButton) {
      muteButton.setAttribute("aria-pressed", muted ? "true" : "false");
      muteButton.setAttribute("aria-label", muted ? config.i18nUnmute : config.i18nMute);
    }
  }

  function setActive(index) {
    if (index === current) {
      syncChrome();
      return;
    }
    current = index;

    slides.forEach(function (slide, i) {
      const video = videoOf(slide);
      if (!video) {
        return;
      }
      if (i === index) {
        video.preload = "auto";
        video.muted = muted;
        const played = video.play();
        if (played && played.catch) {
          // Unmuted autoplay is refused without a prior gesture; play muted
          // and let unlockSound() turn sound on at the first interaction.
          played.catch(function () {
            if (i !== current) {
              return;
            }
            if (!muted) {
              muted = true;
              soundBlocked = true;
              applyMute();
            }
            video.play().catch(function () {
              showPlayButton(true);
            });
          });
        }
        warmNext(index);
      } else {
        video.pause();
      }
    });

    syncChrome();
  }

  function syncChrome() {
    const video = videoOf(slides[current]);

    if (progress) {
      progress.classList.toggle("is_visible", !!video);
    }
    showPlayButton(!!video && video.paused);

    if (prevButton) {
      prevButton.disabled = current === 0;
      prevButton.setAttribute("aria-label", config.i18nPrev + " (" + current + " " + config.i18nOf + " " + slides.length + ")");
    }
    if (nextButton) {
      nextButton.disabled = current === slides.length - 1;
      nextButton.setAttribute("aria-label", config.i18nNext + " (" + (current + 2) + " " + config.i18nOf + " " + slides.length + ")");
    }
    if (progressFill && !video) {
      progressFill.style.width = "0";
    }
  }

  // Start fetching the next slide only once the current one can play through,
  // so it never steals bandwidth from the video being watched.
  function warmNext(index) {
    const video = videoOf(slides[index]);
    const next = videoOf(slides[index + 1]);
    if (!video || !next || next.preload !== "none") {
      return;
    }
    const warm = function () {
      if (next.preload === "none") {
        next.preload = "metadata";
      }
    };
    if (video.readyState >= 4) {
      warm();
    } else {
      video.addEventListener("canplaythrough", warm, { once: true });
    }
  }

  function enableSound(video) {
    soundBlocked = false;
    muted = false;
    applyMute();
    if (video) {
      // They heard nothing so far; restart so the opening question isn't missed.
      video.currentTime = 0;
    }
  }

  function unlockSound(event) {
    if (!soundBlocked) {
      return;
    }
    // The mute and play buttons handle their own click.
    if (event.target && event.target.closest && event.target.closest("[data-mute], [data-play]")) {
      return;
    }
    if (navigator.userActivation && !navigator.userActivation.isActive) {
      return;
    }
    const video = videoOf(slides[current]);
    enableSound(video);
    if (video) {
      video.play().catch(function () {
        // Still refused: go back to muted playback and wait for the next gesture.
        muted = true;
        soundBlocked = true;
        applyMute();
        video.play().catch(function () {});
      });
    }
  }

  ["pointerdown", "pointerup", "touchend", "keydown"].forEach(function (type) {
    document.addEventListener(type, unlockSound, true);
  });

  function showPlayButton(visible) {
    if (playButton) {
      playButton.classList.toggle("is_visible", visible);
    }
  }

  function goTo(index) {
    const target = slides[index];
    if (!target) {
      return;
    }
    target.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  }

  //---------- ACTIVE SLIDE TRACKING ----------
  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            setActive(slides.indexOf(entry.target));
          }
        });
      },
      { root: reel, threshold: 0.6 }
    );
    slides.forEach(function (slide) {
      observer.observe(slide);
    });
  } else {
    setActive(0);
  }

  //---------- SCRUBBER ----------
  slides.forEach(function (slide) {
    const video = videoOf(slide);
    if (!video) {
      return;
    }
    video.addEventListener("timeupdate", function () {
      if (slides.indexOf(slide) !== current || !video.duration) {
        return;
      }
      const ratio = video.currentTime / video.duration;
      if (progressFill) {
        progressFill.style.width = ratio * 100 + "%";
      }
      if (progress) {
        progress.setAttribute("aria-valuenow", Math.round(ratio * 100));
      }
    });
    video.addEventListener("play", function () {
      if (slides.indexOf(slide) === current) {
        showPlayButton(false);
      }
    });
    video.addEventListener("pause", function () {
      if (slides.indexOf(slide) === current) {
        showPlayButton(true);
      }
    });
  });

  function seekTo(clientX) {
    const video = videoOf(slides[current]);
    if (!video || !video.duration || !progress) {
      return;
    }
    const rect = progress.getBoundingClientRect();
    let ratio = (clientX - rect.left) / rect.width;
    ratio = Math.min(Math.max(ratio, 0), 1);
    video.currentTime = ratio * video.duration;
  }

  if (progress) {
    let scrubbing = false;

    progress.addEventListener("pointerdown", function (event) {
      scrubbing = true;
      progress.setPointerCapture(event.pointerId);
      seekTo(event.clientX);
    });
    progress.addEventListener("pointermove", function (event) {
      if (scrubbing) {
        seekTo(event.clientX);
      }
    });
    progress.addEventListener("pointerup", function () {
      scrubbing = false;
    });

    progress.addEventListener("keydown", function (event) {
      const video = videoOf(slides[current]);
      if (!video || !video.duration) {
        return;
      }
      if (event.key === "ArrowRight") {
        video.currentTime = Math.min(video.currentTime + 5, video.duration);
        event.preventDefault();
      } else if (event.key === "ArrowLeft") {
        video.currentTime = Math.max(video.currentTime - 5, 0);
        event.preventDefault();
      }
    });
  }

  //---------- CONTROLS ----------
  if (muteButton) {
    muteButton.addEventListener("click", function () {
      const video = videoOf(slides[current]);
      if (soundBlocked) {
        enableSound(video);
      } else {
        muted = !muted;
        applyMute();
      }
      writeStore(MUTE_KEY, muted ? "1" : "0");
      if (video && video.paused) {
        video.play().catch(function () {});
      }
    });
  }

  if (playButton) {
    playButton.addEventListener("click", function () {
      const video = videoOf(slides[current]);
      if (!video) {
        return;
      }
      if (video.paused) {
        if (soundBlocked) {
          enableSound(video);
        }
        video.play().catch(function () {});
      } else {
        video.pause();
      }
    });
  }

  if (prevButton) {
    prevButton.addEventListener("click", function () {
      goTo(current - 1);
    });
  }
  if (nextButton) {
    nextButton.addEventListener("click", function () {
      goTo(current + 1);
    });
  }

  document.addEventListener("keydown", function (event) {
    // the scrubber owns the arrow keys while focused
    if (event.target && event.target.closest && event.target.closest("[data-progress]")) {
      return;
    }
    if (event.key === "ArrowDown") {
      goTo(current + 1);
      event.preventDefault();
    } else if (event.key === "ArrowUp") {
      goTo(current - 1);
      event.preventDefault();
    } else if (event.key === "Escape" && sheet && sheet.classList.contains("is_open")) {
      sheet.classList.remove("is_open");
    }
  });

  //---------- LIKE ----------
  if (likeButton && config.masterId) {
    likeButton.addEventListener("click", function () {
      if (likeButton.getAttribute("aria-pressed") === "true") {
        return;
      }
      fetch("/fa/rates/score/?type=%2B&object_master_id=" + encodeURIComponent(config.masterId))
        .then(function (response) {
          if (response.status !== 200) {
            throw new Error("rate rejected");
          }
          return response.json();
        })
        .then(function (score) {
          likeButton.setAttribute("aria-pressed", "true");
          if (likeCount) {
            likeCount.textContent = toPersianNumber(score.plus);
          }
        })
        .catch(function () {
          if (errorBox) {
            errorBox.classList.add("is_visible");
            setTimeout(function () {
              errorBox.classList.remove("is_visible");
            }, 3000);
          }
        });
    });
  }

  //---------- SHARE ----------
  if (shareButton) {
    shareButton.addEventListener("click", function () {
      if (navigator.share) {
        navigator
          .share({
            title: config.title,
            text: config.title,
            url: config.shareUrl,
          })
          .catch(function () {});
        return;
      }
      if (sheet) {
        sheet.classList.toggle("is_open");
      }
    });
  }

  if (sheet) {
    sheet.addEventListener("click", function (event) {
      if (!event.target.closest(".story_sheet_inner")) {
        sheet.classList.remove("is_open");
      }
    });

    const shortLink = sheet.querySelector(".short_link");
    if (shortLink) {
      shortLink.addEventListener("click", function () {
        const url = shortLink.getAttribute("data-url");
        const copied = shortLink.querySelector("#copied_txt");
        const done = function () {
          if (copied) {
            copied.classList.add("show_copied");
            setTimeout(function () {
              copied.classList.remove("show_copied");
            }, 2000);
          }
        };
        if (navigator.clipboard) {
          navigator.clipboard.writeText(url).then(done, function () {});
        }
      });
    }
  }

  //---------- AD ----------
  // The slot is as tall as whatever creative loads; publish that height so
  // the caption, rail and scrubber clear it exactly.
  if (ad && stage && "ResizeObserver" in window) {
    new ResizeObserver(function () {
      if (ad.offsetHeight > 0) {
        stage.style.setProperty("--story-ad-h", ad.offsetHeight + "px");
      }
    }).observe(ad);
  }

  if (adClose && ad) {
    adClose.addEventListener("click", function () {
      ad.classList.add("is_closed");
      if (stage) {
        stage.classList.remove("story_stage--has-ad");
      }
    });
  }

  //---------- ONBOARDING ----------
  if (hint && !readStore(HINT_KEY)) {
    hint.classList.add("is_open");
  }
  if (hintDismiss) {
    hintDismiss.addEventListener("click", function () {
      hint.classList.remove("is_open");
      writeStore(HINT_KEY, "1");
    });
  }

  //---------- INIT ----------
  applyMute();
  setActive(0);
  syncChrome();
});
